import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import Preloader from '../components/Preloader';
import Footer from '../components/Footer';
import { FaSearch } from 'react-icons/fa';
import ScrollReveal from '../components/ScrollReveal';

const safaris = [
    {
        id: 1,
        title: 'Shai Hills Resource Reserve',
        region: 'Greater Accra',
        description: 'A wildlife reserve featuring baboons, zebras, and scenic rocky hills near Accra.',
        image: '/assets/shaihills.jpg',
        fullContent: 'Shai Hills Resource Reserve is the closest wildlife reserve to Accra. It combines nature conservation, rich cultural history, and archaeological sites. The reserve is home to troops of olive baboons, kobs, zebras, and ostriches. Visitors can explore the ancestral caves of the Shai people and enjoy rock climbing.',
        wildlife: ['Olive Baboons', 'Zebras', 'Ostriches', 'Kobs', 'Monitor Lizards'],
        images: ['/assets/shaihills.jpg', '/assets/shaihills.jpg', '/assets/shaihills.jpg'],
    },
    {
        id: 2,
        title: 'Aquila Private Game Reserve',
        region: 'Western Cape',
        description: 'A premier Big 5 safari destination offering luxury and wildlife encounters near Cape Town.',
        image: '/assets/accommodation-aquilasafari.avif',
        fullContent: 'Aquila Private Game Reserve offers a Big 5 safari experience just two hours from Cape Town. Set in a private reserve of 10,000 hectares, it is home to lions, leopards, rhinos, elephants, and buffalo. The reserve is dedicated to conservation and offers game drives, horseback safaris, and quad bike safaris.',
        wildlife: ['Lions', 'Leopards', 'Elephants', 'Rhinos', 'Buffalo'],
        images: ['/assets/accommodation-aquilasafari.avif', '/assets/accommodation-aquilasafari.avif', '/assets/accommodation-aquilasafari.avif'],
    },
    {
        id: 3,
        title: 'Addis Ababa Zoo and Botanical Garden',
        region: 'Addis Ababa',
        description: 'Urban wildlife sanctuary featuring Ethiopian endemic species and endangered animals.',
        image: '/assets/Addis_Abeba_Lion_Zoo_gate,_Ethiopia,_2004-11-07.jpg',
        fullContent: 'The Addis Ababa Zoo and Botanical Garden is Africa\'s oldest zoo, established in 1948. The facility houses numerous Ethiopian endemic species and endangered animals in well-designed habitats. Visitors can observe lions, leopards, primates, and various bird species while exploring the beautiful botanical gardens featuring native Ethiopian plants and trees.',
        wildlife: ['Ethiopian Lion', 'Leopards', 'Primates', 'Antelopes', 'Endemic Birds'],
        images: ['/assets/Addis_Abeba_Lion_Zoo_gate,_Ethiopia,_2004-11-07.jpg', '/assets/Addis_Abeba_Lion_Zoo_gate,_Ethiopia,_2004-11-07.jpg', '/assets/Addis_Abeba_Lion_Zoo_gate,_Ethiopia,_2004-11-07.jpg'],
    },
];

const WildlifeSafaris = () => {
    const [filter, setFilter] = useState('All');
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();

    const filteredSafaris = safaris.filter(safari => {
        const matchesRegion = filter === 'All' || safari.region === filter;
        const matchesSearch = safari.title.toLowerCase().includes(searchTerm.toLowerCase()) || safari.description.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesRegion && matchesSearch;
    });

    const regions = ['All', ...new Set(safaris.map(s => s.region))];

    const handleExplore = (safariId) => {
        navigate(`/safari-detail/${safariId}`);
    };

    return (
        <div className='font-nixie-one bg-gray-50 min-h-screen text-gray-800'>
            <PageHeader />
            <Preloader />
            <main>
                {/* Hero Section */}
                <section className="relative h-[50vh] bg-cover bg-center" style={{ backgroundImage: "url('/assets/africasafari.jpg')" }}>
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <div className="text-center text-white px-4">
                            <h1 className="text-5xl md:text-6xl font-cormorant-upright font-bold">Wildlife Safaris</h1>
                            <p className="mt-4 text-sm md:text-sm max-w-3xl mx-auto">Discover Africa's incredible biodiversity across Ghana, South Africa, and Ethiopia, and encounter the continent's most magnificent wildlife in their natural habitats.</p>
                        </div>
                    </div>
                </section>

                {/* Safaris Section */}
                <section className="py-20 px-4">
                    <div className="container mx-auto">
                        <div className="flex flex-col md:flex-row justify-between items-center mb-12">
                            <div className="flex space-x-2 md:space-x-4 mb-6 md:mb-0 flex-wrap">
                                {regions.map(region => (
                                    <button key={region} onClick={() => setFilter(region)} className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors duration-300 ${filter === region ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
                                        {region}
                                    </button>
                                ))}
                            </div>
                            <div className="relative w-96 md:w-96">
                                <input type="text" placeholder="Search wildlife areas..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-1 text-sm border rounded-full focus:outline-none focus:ring-2 focus:ring-green-500" />
                                <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                            </div>
                        </div>

                        <ScrollReveal animationClass="animate-slide-left" threshold={0.1} delay={200}>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {filteredSafaris.map((safari) => (
                                    <div key={safari.id} className="bg-white rounded-lg shadow-lg overflow-hidden flex flex-col group">
                                        <div className="relative h-40">
                                            <img src={safari.image} alt={safari.title} className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500" />
                                            <span className="absolute top-2 right-2 bg-green-600 text-white text-xs font-bold px-2 py-1 rounded">{safari.region}</span>
                                        </div>
                                        <div className="p-6 flex flex-col flex-grow">
                                            <h3 className="text-xl font-bold font-cormorant-upright mb-2">{safari.title}</h3>
                                            <p className="text-gray-600 text-sm flex-grow">{safari.description}</p>
                                            <button onClick={() => handleExplore(safari.id)} className="inline-block mt-4 self-start px-5 py-1 text-sm bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors duration-300">
                                                Explore
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </ScrollReveal>
                        {filteredSafaris.length === 0 && (
                            <p className="text-center text-gray-500 mt-8">No wildlife areas found matching your criteria.</p>
                        )}
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
};

export default WildlifeSafaris;
